// useMatches.js
import { useState, useCallback, useRef } from "react";
import { getMatches } from "@/services";

const useMatches = () => {
  const [matches, setMatches] = useState([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isMatchesLoading, setIsMatchesLoading] = useState(false);
  const requestIdRef = useRef(0);

  const fetchMatches = useCallback(async (query = {}) => {
    const { wantAdId, inventoryId, page = 1 } = query;
    if (!wantAdId && !inventoryId) return;

    const myRequestId = ++requestIdRef.current;
    // Stale-while-revalidate: only show the skeleton when there is no list
    // yet. Re-fetches (tab switch, refresh, pagination > 1) keep the
    // existing list visible and atomically replace it when the new payload
    // lands. Prevents a skeleton flash on every Matches tab click.
    setMatches((prev) => {
      if (prev.length === 0) setIsMatchesLoading(true);
      return prev;
    });
    const queryWithScore = { minScore: 40, ...query };

    try {
      const { data } = await getMatches(queryWithScore);

      if (myRequestId !== requestIdRef.current) return;

      const {
        data: payload = [],
        totalCount = 0,
        hasNextPage: nextPage = false,
      } = data || {};

      setMatches((prev) => (page > 1 ? [...prev, ...payload] : payload));
      setTotalMatches(totalCount);
      setHasNextPage(nextPage);
    } catch {
      if (myRequestId !== requestIdRef.current) return;
      if (page === 1) setMatches([]);
      setHasNextPage(false);
    } finally {
      if (myRequestId === requestIdRef.current) {
        setIsMatchesLoading(false);
      }
    }
  }, []);

  return { matches, totalMatches, hasNextPage, isMatchesLoading, fetchMatches };
};

export default useMatches;
